/**
 * 预渲染就绪信号
 *
 * prerender-spa-plugin 用 PuppeteerRenderer 打开页面后，需要知道「什么时候页面才算渲染完」。
 * 纯靠定时（renderAfterTime）不可靠：设短了接口还没回来，预渲染出空壳；设长了每个页面都白等。
 *
 * 这里用「在途请求数 + 静默期」判定：
 *   1. axios 请求发出 -> pending +1；返回或失败 -> pending -1（由 request.js 拦截器调用）
 *   2. pending 归零后连续 SILENT_MS 没有新请求，且 Vue 已完成首屏挂载 -> 触发 render-event
 *
 * 只影响构建期：普通浏览器访问时 document.dispatchEvent 无人监听，成本可忽略。
 */

var pending = 0
var silentTimer = null
var fired = false
var routerReady = false

// 静默期：请求归零后再等这么久没有新请求，才认为页面稳定
var SILENT_MS = 600
// 硬上限：超过这个时间无条件触发，避免某个接口挂死把构建卡住
// 要小于 vue.config.js 里 PrerenderSPAPlugin 的 renderAfterTime（12000），
// 否则每次都走插件超时、白等一轮，构建时间被成倍拉长
var HARD_LIMIT_MS = 9000

var EVENT_NAME = 'render-event'

function tryFire() {
  if (fired) return
  if (!routerReady) return
  if (pending > 0) return

  clearTimeout(silentTimer)
  silentTimer = setTimeout(function () {
    if (fired) return
    // 静默期结束后再确认一次，防止期间又发了新请求
    if (pending > 0) return
    fire()
  }, SILENT_MS)
}

function fire() {
  if (fired) return
  fired = true
  clearTimeout(silentTimer)
  document.dispatchEvent(new Event(EVENT_NAME))
}

/** 请求开始 */
export function requestStart() {
  pending++
  // 有新请求进来，取消上一次的静默倒计时
  clearTimeout(silentTimer)
}

/** 请求结束（成功或失败都要调） */
export function requestEnd() {
  if (pending > 0) pending--
  tryFire()
}

/**
 * 最小观察窗口。
 *
 * 不能直接拿 router.onReady 当就绪信号：路由组件是异步 chunk，
 * onReady 时组件可能还在下载，此时 pending 为 0，会立刻误判「页面已稳定」。
 * 所以先等一个窗口，把 chunk 下载 + mounted 里发出的首屏请求都放进来，再开始观察静默期。
 */
var MIN_WAIT_MS = 1200

/** 路由首次就绪，由 main.js 调用 */
export function markRouterReady() {
  routerReady = true
  tryFire()
}

/**
 * 硬超时兜底。
 * 由 main.js 在挂载时启动，确保即使某个接口永久 pending，构建也不会卡死。
 */
export function startHardLimit() {
  setTimeout(fire, HARD_LIMIT_MS)
}

/**
 * 统一入口，在 main.js 挂载后调用一次即可。
 * @param {Object} router vue-router 实例
 */
export function installPrerenderReady(router) {
  startHardLimit()

  router.onReady(function () {
    // 等一个窗口再开始观察，避开异步 chunk 加载的空档期
    setTimeout(markRouterReady, MIN_WAIT_MS)
  })

  // 兜底：万一 onReady 因故未触发（如重定向异常），也要开始观察
  setTimeout(function () {
    if (!routerReady) markRouterReady()
  }, MIN_WAIT_MS + 3000)
}

/** 供调试：在浏览器控制台查看当前在途请求数 */
export function getPending() {
  return pending
}
